import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Users, Youtube, BarChart3, ArrowLeft, Shield, Tag, Activity, DollarSign, Gift, MessageSquare, Store, Calendar } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/admin', label: 'Overview', icon: BarChart3, exact: true },
  { path: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
  { path: '/admin/billing', label: 'Billing', icon: DollarSign },
  { path: '/admin/promo-codes', label: 'Promo Codes', icon: Gift },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/categories', label: 'Categories', icon: Tag },
  { path: '/admin/occasions', label: 'Occasions', icon: Calendar },
  { path: '/admin/brands', label: 'Brands', icon: Store },
  { path: '/admin/youtube', label: 'Knowledge Base', icon: Youtube },
  { path: '/admin/services', label: 'Services', icon: Activity },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (item) => 
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <div className="min-h-screen bg-cream">
      {/* Admin Top Bar */}
      <div className="bg-charcoal text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-sage" />
            <span className="font-medium text-sm tracking-wide">Admin Console</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-400">{user?.email}</span>
            <button
              onClick={() => navigate('/stash')}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              data-testid="admin-back-to-app"
            >
              <ArrowLeft size={14} />
              Back to App
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
        {/* Mobile Nav - above content */}
        <div className="md:hidden mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${
                    active
                      ? 'bg-charcoal text-white font-medium'
                      : 'bg-white text-charcoal shadow-sm'
                  }`}
                >
                  <Icon size={14} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - desktop only */}
          <nav className="w-48 flex-shrink-0 hidden md:block">
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      active
                        ? 'bg-charcoal text-white font-medium'
                        : 'text-charcoal hover:bg-white hover:shadow-sm'
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon size={16} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
